import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import StatusBadge from '../components/StatusBadge'
import { api } from '../lib/api'
import { M_LABELS, P_LABELS } from '../lib/types'
import type { MType, PType } from '../lib/types'

const mTypes: MType[] = ['M1', 'M2', 'M3', 'M4', 'M5', 'M6']
const pTypes: PType[] = ['P1', 'P3', 'P4', 'P5', 'P6a', 'P6b', 'P6c', 'P7', 'P8', 'P9']

// Products that are implemented
const implementedProducts = ['P1', 'P3', 'P4', 'P5', 'P6a', 'P6b', 'P6c', 'P7', 'P8', 'P9']

// Which ones are mechanical (no AI cost)
const mechanicalTypes = new Set(['M1', 'P3', 'P5'])

export default function LectureDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [lecture, setLecture] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [fetchingTranscript, setFetchingTranscript] = useState(false)
  const [manufacturing, setManufacturing] = useState(false)
  const [fullProcessing, setFullProcessing] = useState(false)
  const [producingType, setProducingType] = useState<string | null>(null)
  const [actionLog, setActionLog] = useState<string[]>([])

  function log(msg: string) {
    setActionLog((prev) => [...prev, `[${new Date().toLocaleTimeString('ar-EG')}] ${msg}`])
  }

  async function reload() {
    if (!id) return
    const data = await api.getLecture(id)
    setLecture(data)
  }

  useEffect(() => {
    if (!id) return
    async function load() {
      try {
        const data = await api.getLecture(id!)
        setLecture(data)
      } catch (err: any) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id])

  async function handleFetchTranscript() {
    if (!id) return
    setFetchingTranscript(true)
    log('جاري سحب الترانسكريبت (كل اللغات)...')
    try {
      const result = await api.fetchTranscript(id) as any
      const langs = result.languages?.join(', ') || '?'
      log(`تم! ${result.tracks_count} لغة: ${langs}`)
      await reload()
    } catch (err: any) {
      log(`خطأ: ${err.message}`)
    } finally {
      setFetchingTranscript(false)
    }
  }

  async function handleManufacture() {
    if (!id) return
    setManufacturing(true)
    log('جاري تشغيل التصنيع M1-M6...')
    try {
      const existing = (lecture?.manufacturing || [])
        .filter((m: any) => m.status === 'complete')
        .map((m: any) => m.type)
      const missing = mTypes.filter((m) => !existing.includes(m))

      if (missing.length === 0) {
        log('كل الـ M1-M6 موجودين')
        setManufacturing(false)
        return
      }

      log(`ناقص: ${missing.join(', ')}`)
      const result = await api.manufacture(id, missing) as any
      for (const r of result.results || []) {
        if (r.status === 'complete') {
          const mech = mechanicalTypes.has(r.type) ? ' (ميكانيكي)' : ''
          log(`${r.type}${mech} — ${r.model} — $${r.cost?.toFixed(4) || '0'}`)
        } else if (r.status === 'already_exists') {
          log(`${r.type} موجود`)
        } else {
          log(`خطأ ${r.type}: ${r.error || 'فشل'}`)
        }
      }
      await reload()
    } catch (err: any) {
      log(`خطأ: ${err.message}`)
    } finally {
      setManufacturing(false)
    }
  }

  async function handleProduceProduct(pType: string) {
    if (!id) return
    setProducingType(pType)
    const mech = mechanicalTypes.has(pType) ? ' (ميكانيكي)' : ''
    log(`جاري إنتاج ${pType}${mech}...`)
    try {
      const result = await api.generateProduct(id, pType) as any
      if (result.status === 'already_exists') {
        log(`${pType} موجود`)
      } else {
        log(`${pType} — ${result.model} — $${result.cost?.toFixed(4) || '0'}`)
      }
      await reload()
    } catch (err: any) {
      log(`خطأ ${pType}: ${err.message}`)
    } finally {
      setProducingType(null)
    }
  }

  async function handleRetry(types: string[]) {
    if (!id) return
    log(`إعادة تشغيل: ${types.join(', ')}...`)
    try {
      const result = await api.retryProcess(id, types) as any
      log(`تمت الإعادة: ${JSON.stringify(result.results?.length || 0)} عناصر`)
      await reload()
    } catch (err: any) {
      log(`خطأ: ${err.message}`)
    }
  }

  async function handleFullProcess() {
    if (!id) return
    setFullProcessing(true)
    log('معالجة كاملة — ترانسكريبت + M1-M6 + منتجات...')
    try {
      // Step 1: Transcript
      const rawTypes = (lecture?.raw_materials || []).map((r: any) => r.type)
      const hasAnyTranscript = rawTypes.some((t: string) => t.startsWith('transcript_'))

      if (!hasAnyTranscript) {
        log('سحب الترانسكريبت...')
        const tResult = await api.fetchTranscript(id) as any
        log(`ترانسكريبت: ${tResult.tracks_count} لغة`)
        await reload()
      }

      // Step 2: Manufacture
      log('بدء التصنيع M1-M6...')
      const mResult = await api.manufacture(id) as any
      for (const r of mResult.results || []) {
        if (r.status === 'complete') {
          log(`${r.type} — $${r.cost?.toFixed(4) || '0'}`)
        } else if (r.status === 'already_exists') {
          log(`${r.type} موجود`)
        } else {
          log(`خطأ ${r.type}: ${r.error || 'فشل'}`)
        }
      }

      // Step 3: Products
      log('بدء إنتاج المنتجات...')
      await reload() // refresh to get latest M status
      for (const pType of implementedProducts) {
        const existingP = (lecture?.products || []).find((p: any) => p.type === pType && p.status === 'complete')
        if (existingP) {
          log(`${pType} موجود`)
          continue
        }
        try {
          log(`${pType}...`)
          const pResult = await api.generateProduct(id, pType) as any
          log(`${pType} — $${pResult.cost?.toFixed(4) || '0'}`)
        } catch (err: any) {
          log(`خطأ ${pType}: ${err.message}`)
        }
      }

      await reload()
      log('المعالجة الكاملة انتهت!')
    } catch (err: any) {
      log(`خطأ: ${err.message}`)
    } finally {
      setFullProcessing(false)
    }
  }

  if (loading) return <div className="text-center text-gray-400 py-12">جاري التحميل...</div>
  if (error) return <div className="text-center text-red-400 py-12">خطأ: {error}</div>
  if (!lecture) return <div className="text-center text-red-400 py-12">المحاضرة غير موجودة</div>

  const mStatus: Record<string, string> = {}
  if (lecture.manufacturing) {
    for (const m of lecture.manufacturing) mStatus[m.type] = m.status
  }
  const pStatus: Record<string, string> = {}
  if (lecture.products) {
    for (const p of lecture.products) pStatus[p.type] = p.status
  }

  const rawTypes: string[] = (lecture.raw_materials || []).map((r: any) => r.type)
  const transcriptOrder: Record<string, number> = { transcript_ar: 0, transcript_en: 1, transcript_ar_en: 2 }
  const transcriptLangs = rawTypes.filter((t: string) => t.startsWith('transcript_')).sort((a, b) => (transcriptOrder[a] ?? 9) - (transcriptOrder[b] ?? 9))
  const hasTranscript = transcriptLangs.length > 0
  const mDone = Object.values(mStatus).filter(s => s === 'complete').length
  const pDone = Object.values(pStatus).filter(s => s === 'complete').length
  const isProcessing = fetchingTranscript || manufacturing || fullProcessing || producingType !== null

  // Find failed items for retry button
  const failedM = (lecture.manufacturing || []).filter((m: any) => m.status === 'error').map((m: any) => m.type)
  const failedP = (lecture.products || []).filter((p: any) => p.status === 'error').map((p: any) => p.type)
  const hasFailed = failedM.length > 0 || failedP.length > 0

  return (
    <div>
      <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
        <button onClick={() => navigate(-1)} className="hover:text-vertex-gold">رجوع</button>
        <span>←</span>
        <span className="text-white">L{lecture.lecture_number}</span>
        {lecture.course_name && <span className="text-gray-600 text-xs">({lecture.course_name})</span>}
      </div>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-vertex-gold">{lecture.title_ar || `محاضرة ${lecture.lecture_number}`}</h2>
          {lecture.title_en && <p className="text-gray-400 text-sm" dir="ltr">{lecture.title_en}</p>}
          {lecture.youtube_id && (
            <p className="text-xs text-gray-500 mt-1" dir="ltr">
              YouTube: <span className="font-mono text-gray-400">{lecture.youtube_id}</span>
            </p>
          )}
        </div>
        <StatusBadge status={lecture.status || 'raw'} />
      </div>

      {/* ==================== Raw Materials (Transcripts) ==================== */}
      <div className="bg-vertex-navy rounded-lg border border-vertex-surface p-4 mb-4">
        <h3 className="text-sm font-bold mb-3">المادة الخام — الترانسكريبت</h3>
        <div className="flex gap-3 flex-wrap">
          {transcriptLangs.length > 0 ? transcriptLangs.map((type: string) => {
            const langCode = type.replace('transcript_', '')
            const langLabel = langCode === 'ar' ? 'عربي' : langCode === 'en' ? 'English' : langCode === 'ar_en' ? 'عربي + English' : langCode.toUpperCase()
            return (
              <div key={type} className="flex items-center gap-2 bg-vertex-surface px-3 py-2 rounded text-xs">
                <span className="text-green-400">●</span>
                <span>{langLabel}</span>
                {/* Download buttons */}
                <button
                  onClick={() => id && api.downloadOutput(id, type, 'md')}
                  className="text-vertex-gold hover:text-vertex-gold-light ml-2"
                  title="تنزيل MD"
                >
                  MD
                </button>
                <button
                  onClick={() => id && api.downloadOutput(id, type, 'txt')}
                  className="text-gray-500 hover:text-gray-300"
                  title="تنزيل TXT"
                >
                  TXT
                </button>
              </div>
            )
          }) : (
            <span className="text-xs text-gray-600">لا يوجد ترانسكريبت</span>
          )}

          {lecture.youtube_id && (
            <button
              onClick={handleFetchTranscript}
              disabled={isProcessing}
              className="bg-vertex-gold text-vertex-deep px-4 py-2 rounded text-xs font-bold hover:bg-vertex-gold-light disabled:opacity-50 transition-colors"
            >
              {fetchingTranscript ? 'جاري السحب...' : hasTranscript ? 'إعادة السحب' : 'سحب الترانسكريبت'}
            </button>
          )}
        </div>
      </div>

      {/* ==================== Manufacturing M1-M6 ==================== */}
      <div className="bg-vertex-navy rounded-lg border border-vertex-surface p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold">مرحلة التصنيع (M1-M6)</h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">{mDone}/5</span>
            {hasTranscript && mDone < 5 && (
              <button
                onClick={handleManufacture}
                disabled={isProcessing}
                className="bg-vertex-gold text-vertex-deep px-3 py-1 rounded text-xs font-bold hover:bg-vertex-gold-light disabled:opacity-50 transition-colors"
              >
                {manufacturing ? 'جاري التصنيع...' : 'صنّع الناقص'}
              </button>
            )}
            {mDone === 5 && <span className="text-green-400 text-xs font-bold">مكتمل</span>}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {mTypes.map((m) => {
            const status = mStatus[m] || 'pending'
            const label = M_LABELS[m]
            const isMech = mechanicalTypes.has(m)
            return (
              <div
                key={m}
                className={`p-3 rounded text-center border transition-colors ${
                  status === 'complete' ? 'border-green-800 bg-green-900/20'
                    : status === 'processing' ? 'border-blue-800 bg-blue-900/20 animate-pulse'
                    : status === 'error' ? 'border-red-800 bg-red-900/20'
                    : 'border-vertex-surface bg-vertex-surface/30'
                }`}
              >
                <div className="text-xs font-mono text-vertex-gold mb-1">{m}</div>
                <div className="text-xs text-gray-300">{label.ar}</div>
                {isMech && <div className="text-[10px] text-green-600 mt-0.5">ميكانيكي</div>}
                <div className="mt-1 flex items-center justify-center gap-1">
                  {status === 'complete' ? (
                    <>
                      <span className="text-green-400 text-xs">●</span>
                      <button
                        onClick={() => id && api.downloadOutput(id, m, 'md')}
                        className="text-[10px] text-vertex-gold hover:text-vertex-gold-light"
                      >
                        MD
                      </button>
                    </>
                  ) : status === 'error' ? (
                    <button
                      onClick={() => handleRetry([m])}
                      className="text-[10px] text-red-400 hover:text-red-300"
                    >
                      إعادة
                    </button>
                  ) : (
                    <span className="text-gray-600 text-xs">○</span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* ==================== Products P1-P9 ==================== */}
      <div className="bg-vertex-navy rounded-lg border border-vertex-surface p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold">المنتجات (P1-P9)</h3>
          <span className="text-xs text-gray-400">{pDone}/{implementedProducts.length}</span>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {pTypes.map((p) => {
            const status = pStatus[p] || 'pending'
            const label = P_LABELS[p]
            const implemented = implementedProducts.includes(p)
            const isProducing = producingType === p
            const isMech = mechanicalTypes.has(p)
            return (
              <div
                key={p}
                className={`p-3 rounded border transition-colors ${
                  status === 'complete' ? 'border-green-800 bg-green-900/20'
                    : isProducing ? 'border-blue-800 bg-blue-900/20 animate-pulse'
                    : status === 'error' ? 'border-red-800 bg-red-900/20'
                    : 'border-vertex-surface bg-vertex-surface/30'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-mono text-vertex-gold">{p}</span>
                  {status === 'complete' ? (
                    <div className="flex items-center gap-1">
                      <span className="text-green-400 text-xs">●</span>
                      <button
                        onClick={() => id && api.downloadOutput(id, p, 'md')}
                        className="text-[10px] text-vertex-gold hover:text-vertex-gold-light"
                      >
                        MD
                      </button>
                    </div>
                  ) : isProducing ? (
                    <span className="text-blue-400 text-xs">...</span>
                  ) : status === 'error' ? (
                    <button
                      onClick={() => handleRetry([p])}
                      className="text-[10px] text-red-400 hover:text-red-300"
                    >
                      إعادة
                    </button>
                  ) : (
                    <span className="text-gray-600 text-xs">○</span>
                  )}
                </div>
                <div className="text-xs text-gray-300">{label.ar}</div>
                {isMech && <div className="text-[10px] text-green-600">ميكانيكي</div>}
                {status !== 'complete' && mDone >= 1 && implemented && (
                  <button
                    onClick={() => handleProduceProduct(p)}
                    disabled={isProcessing}
                    className="w-full mt-2 text-xs bg-vertex-surface hover:bg-vertex-gold/20 text-vertex-gold px-2 py-1 rounded transition-colors disabled:opacity-50"
                  >
                    {isProducing ? '...' : 'إنتاج'}
                  </button>
                )}
                {!implemented && status !== 'complete' && (
                  <span className="text-[10px] text-gray-600 block mt-2">قريباً</span>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* ==================== Actions ==================== */}
      <div className="flex gap-3 flex-wrap mb-4">
        <button
          onClick={handleFullProcess}
          disabled={isProcessing}
          className="bg-vertex-gold text-vertex-deep px-6 py-2 rounded text-sm font-bold hover:bg-vertex-gold-light disabled:opacity-50 transition-colors"
        >
          {fullProcessing ? 'جاري المعالجة...' : 'معالجة كاملة'}
        </button>

        {hasFailed && (
          <button
            onClick={() => handleRetry([...failedM, ...failedP])}
            disabled={isProcessing}
            className="border border-red-800 text-red-400 px-4 py-2 rounded text-sm hover:bg-red-900/20 transition-colors disabled:opacity-50"
          >
            إعادة الفاشل ({failedM.length + failedP.length})
          </button>
        )}

        <button
          onClick={() => id && api.exportLecture(id)}
          disabled={isProcessing || pDone === 0}
          className="border border-vertex-surface text-gray-400 px-4 py-2 rounded text-sm hover:text-white hover:border-vertex-gold/30 transition-colors disabled:opacity-50"
        >
          تصدير JSON
        </button>
      </div>

      {/* ==================== Action Log ==================== */}
      {actionLog.length > 0 && (
        <div className="bg-vertex-deep rounded-lg border border-vertex-surface p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-400">سجل العمليات</h3>
            <button onClick={() => setActionLog([])} className="text-xs text-gray-600 hover:text-gray-400">مسح</button>
          </div>
          <div className="space-y-1 max-h-48 overflow-y-auto text-xs font-mono" dir="rtl">
            {actionLog.map((msg, i) => (
              <div key={i} className={`${msg.includes('خطأ') ? 'text-red-400' : msg.includes('تم') || msg.includes('مكتمل') || msg.includes('انتهت') ? 'text-green-400' : 'text-gray-400'}`}>
                {msg}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
